import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import StatsBar from '../ui/StatsBar';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatDateTime = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
};

const formatDaysAgo = (date) => {
  if (!date) return '-';
  const now = new Date();
  const then = new Date(date);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays} days ago`;
};

const getAutotaskUrl = (autotaskId) => {
  return `https://ww29.autotask.net/Mvc/CRM/AccountDetail.mvc?ids%5B0%5D=${autotaskId}&AccountId=${autotaskId}`;
};

const getHubSpotCompanyUrl = (hubspotId) => {
  return `https://app.hubspot.com/contacts/23367316/company/${hubspotId}`;
};

const getSyncStatusLabel = (status) => {
  const labels = {
    success: 'Success',
    synced: 'Synced',
    pending: 'Pending',
    processing: 'Processing',
    error: 'Error'
  };
  return labels[status] || 'Unknown';
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const Companies = ({ companies, stats, loading, pagination, fetchCompanies, apiFetch }) => {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [syncHistory, setSyncHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingRetry, setLoadingRetry] = useState(false);
  const [retryResult, setRetryResult] = useState(null);

  const [filters, setFilters] = useState({
    syncStatus: 'all',
    hasOpportunities: 'all',
    searchTerm: ''
  });

  const [sortConfig, setSortConfig] = useState({
    field: 'last_sync_at',
    direction: 'desc'
  });

  // Pagination state - synced with server
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Server-side search with debounce
  const [serverSearchTerm, setServerSearchTerm] = useState('');
  const searchTimeoutRef = useRef(null);

  // Debounced search handler - triggers API call after 500ms of no typing
  const handleSearchChange = useCallback((value) => {
    setFilters(prev => ({ ...prev, searchTerm: value }));

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      setServerSearchTerm(value);
      setCurrentPage(1);
      fetchCompanies(1, itemsPerPage, value);
    }, 500);
  }, [fetchCompanies, itemsPerPage]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // ===========================================================================
  // MODAL FUNCTIONS
  // ===========================================================================

  const closeModal = () => {
    setShowDetailsModal(false);
    setSelectedCompany(null);
    setSyncHistory(null);
    setRetryResult(null);
  };

  const loadSyncHistory = async (companyId) => {
    setLoadingHistory(true);
    try {
      const response = await apiFetch(`/companies/${companyId}/history`);
      const result = await response.json();
      if (result.success) {
        setSyncHistory(result.history);
      }
    } catch (error) {
      console.error('Error loading sync history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleRetrySync = async (companyId) => {
    setLoadingRetry(true);
    setRetryResult(null);
    try {
      const response = await apiFetch(`/companies/${companyId}/retry`, { method: 'POST' });
      const result = await response.json();
      setRetryResult(result);
      // Refresh history after a short delay to show the new sync attempt
      setTimeout(() => {
        loadSyncHistory(companyId);
      }, 2000);
    } catch (error) {
      console.error('Error triggering retry:', error);
      setRetryResult({ success: false, error: error.message });
    } finally {
      setLoadingRetry(false);
    }
  };

  // ===========================================================================
  // FILTERING LOGIC
  // ===========================================================================

  const filteredCompanies = useMemo(() => {
    // Search is now done server-side, only apply local filters
    return companies.filter(company => {
      // Sync status filter
      if (filters.syncStatus !== 'all' && company.sync_status !== filters.syncStatus) {
        return false;
      }

      // Has opportunities filter
      if (filters.hasOpportunities === 'yes' && (company.opportunity_count || 0) === 0) {
        return false;
      }
      if (filters.hasOpportunities === 'no' && (company.opportunity_count || 0) > 0) {
        return false;
      }

      return true;
    });
  }, [companies, filters]);

  // ===========================================================================
  // SORTING LOGIC
  // ===========================================================================

  const sortedCompanies = useMemo(() => {
    return [...filteredCompanies].sort((a, b) => {
      let aValue = a[sortConfig.field];
      let bValue = b[sortConfig.field];

      // Handle null, undefined, and empty string values
      const aEmpty = aValue === null || aValue === undefined || aValue === '';
      const bEmpty = bValue === null || bValue === undefined || bValue === '';
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;  // Empty values go to end
      if (bEmpty) return -1;

      // Handle dates
      if (sortConfig.field === 'created_at' || sortConfig.field === 'last_sync_at' || sortConfig.field === 'last_synced_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
        // Handle invalid dates
        if (isNaN(aValue.getTime())) return 1;
        if (isNaN(bValue.getTime())) return -1;
      }

      // Handle numbers
      if (sortConfig.field === 'opportunity_count') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }

      // Compare
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredCompanies, sortConfig]);

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // ===========================================================================
  // PAGINATION LOGIC (Server-side)
  // ===========================================================================

  const totalPages = pagination?.total_pages || 1;

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchCompanies(page, itemsPerPage, serverSearchTerm);
  };

  const handleItemsPerPageChange = (newPerPage) => {
    setItemsPerPage(newPerPage);
    setCurrentPage(1);
    fetchCompanies(1, newPerPage, serverSearchTerm);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const clearFilters = () => {
    setFilters({
      syncStatus: 'all',
      hasOpportunities: 'all',
      searchTerm: ''
    });
    // Also clear server-side search and refresh
    if (serverSearchTerm) {
      setServerSearchTerm('');
      setCurrentPage(1);
      fetchCompanies(1, itemsPerPage, '');
    }
  };

  // ===========================================================================
  // ENHANCED STATS FOR BOSS VIEW
  // ===========================================================================

  const enhancedStats = {
    ...stats,
    entityType: 'Companies',
    customStats: [
      { label: 'With Opportunities', value: stats.with_opportunities, color: '#10B981' },
      { label: 'Fully Synced', value: stats.fully_synced, color: '#8B5CF6' },
      { label: 'Recently Active (30d)', value: stats.recently_active, color: '#059669' },
      { label: 'HubSpot Coverage', value: `${stats.hubspot_coverage}%`, color: stats.hubspot_coverage > 90 ? '#10B981' : '#F59E0B' },
      { label: 'Sync Coverage', value: `${stats.sync_coverage}%`, color: stats.sync_coverage > 90 ? '#10B981' : '#F59E0B' }
    ]
  };

  // ===========================================================================
  // RENDER
  // ===========================================================================

  return (
    <>
      {/* Stats Bar */}
      <StatsBar stats={enhancedStats} />

      {/* Filters Section */}
      <div className="filters-section">
        <input
          type="text"
          className="search-bar"
          placeholder="🔍 Search by name, Autotask ID, or HubSpot ID... (searches all pages)"
          value={filters.searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
        />

        <div className="filters-row">
          <select
            className="filter-select"
            value={filters.syncStatus}
            onChange={(e) => setFilters({ ...filters, syncStatus: e.target.value })}
          >
            <option value="all">All Statuses</option>
            <option value="success">Success</option>
            <option value="synced">Synced</option>
            <option value="processing">Processing</option>
            <option value="pending">Pending</option>
            <option value="error">Error</option>
          </select>

          <select
            className="filter-select"
            value={filters.hasOpportunities}
            onChange={(e) => setFilters({ ...filters, hasOpportunities: e.target.value })}
          >
            <option value="all">All Companies</option>
            <option value="yes">With Opportunities</option>
            <option value="no">Without Opportunities</option>
          </select>

          {(filters.searchTerm || filters.syncStatus !== 'all' || filters.hasOpportunities !== 'all') && (
            <button className="btn-clear-filters" onClick={clearFilters}>
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className="table-section">
        <div className="table-header">
          <div className="table-title">
            {loading
              ? 'Loading...'
              : `Showing ${sortedCompanies.length} of ${pagination?.total || stats.total} companies (Page ${currentPage} of ${totalPages})`}
          </div>
          <div className="table-actions">
            <button className="icon-btn-small" onClick={() => fetchCompanies(currentPage, itemsPerPage, serverSearchTerm)}>
              🔄 Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            Loading companies...
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th className="sortable-header" onClick={() => handleSort('Name')}>
                  Company Name {sortConfig.field === 'Name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="sortable-header" onClick={() => handleSort('opportunity_count')}>
                  Opportunities {sortConfig.field === 'opportunity_count' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="sortable-header" onClick={() => handleSort('created_at')}>
                  Created {sortConfig.field === 'created_at' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th>Autotask ID</th>
                <th>HubSpot ID</th>
                <th className="sortable-header" onClick={() => handleSort('sync_status')}>
                  Sync Status {sortConfig.field === 'sync_status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="sortable-header" onClick={() => handleSort('last_sync_at')}>
                  Last Sync {sortConfig.field === 'last_sync_at' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedCompanies.map((company, index) => (
                <tr key={company.id || index}>
                  <td title={company.Name}>
                    <strong>{company.Name || '-'}</strong>
                    {company.is_fully_synced && (
                      <span style={{ marginLeft: 8, color: '#10B981', fontSize: 12 }}>✓</span>
                    )}
                  </td>
                  <td>
                    <span className={company.opportunity_count > 0 ? 'opportunity-count active' : 'opportunity-count'}>
                      {company.opportunity_count || 0}
                    </span>
                  </td>
                  <td>{formatDate(company.created_at)}</td>
                  <td>
                    {company.autotask_id ? (
                      <a
                        href={getAutotaskUrl(company.autotask_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="autotask-link"
                      >
                        {company.autotask_id} 🔗
                      </a>
                    ) : (
                      <span style={{ color: '#9CA3AF' }}>-</span>
                    )}
                  </td>
                  <td>
                    {company.hubspot_id ? (
                      <a
                        href={getHubSpotCompanyUrl(company.hubspot_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="autotask-link"
                      >
                        {company.hubspot_id} 🔗
                      </a>
                    ) : (
                      <span style={{ color: '#9CA3AF' }}>-</span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${company.sync_status || 'pending'}`}>
                      {getSyncStatusLabel(company.sync_status)}
                    </span>
                  </td>
                  <td>
                    {company.last_sync_at ? (
                      <span title={formatDateTime(company.last_sync_at)}>
                        {formatDaysAgo(company.last_sync_at)}
                        {!company.is_recently_active && (
                          <span className="stale-indicator" title="No activity in 30+ days"></span>
                        )}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    <button
                      className="action-btn"
                      onClick={() => {
                        setSelectedCompany(company);
                        setShowDetailsModal(true);
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination Controls */}
        {!loading && pagination && (
          <div className="pagination">
            <div className="pagination-info">
              Page {currentPage} of {totalPages} ({pagination.total} total items)
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                style={{ marginLeft: 12, padding: '4px 8px', borderRadius: 6 }}
              >
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
                <option value={200}>200 per page</option>
              </select>
            </div>
            <div className="pagination-controls">
              <button
                className="page-btn"
                onClick={() => handlePageChange(1)}
                disabled={!pagination.has_prev}
              >
                First
              </button>
              <button
                className="page-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!pagination.has_prev}
              >
                Prev
              </button>
              {getPageNumbers().map((page, idx) => (
                page === '...' ? (
                  <span key={`ellipsis-${idx}`} style={{ padding: '10px 8px' }}>...</span>
                ) : (
                  <button
                    key={page}
                    className={`page-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                )
              ))}
              <button
                className="page-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!pagination.has_next}
              >
                Next
              </button>
              <button
                className="page-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={!pagination.has_next}
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {/* DETAILS MODAL */}
      {showDetailsModal && selectedCompany && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedCompany.Name}</h2>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>

            <div className="modal-body">
              {/* Business Details Section */}
              <div className="detail-section">
                <h3>Business Details</h3>
                <div className="detail-row">
                  <strong>Company Name:</strong> {selectedCompany.Name}
                </div>
                <div className="detail-row">
                  <strong>Opportunities:</strong> {selectedCompany.opportunity_count || 0}
                </div>
                <div className="detail-row">
                  <strong>Created:</strong> {formatDateTime(selectedCompany.created_at)}
                </div>
                <div className="detail-row">
                  <strong>Status:</strong>{' '}
                  {selectedCompany.is_fully_synced ? (
                    <span style={{ color: '#10B981', fontWeight: 600 }}>✓ Fully Synced</span>
                  ) : (
                    <span style={{ color: '#F59E0B', fontWeight: 600 }}>⚠ Partial Sync</span>
                  )}
                </div>
              </div>

              {/* System Links Section */}
              <div className="detail-section">
                <h3>System Links</h3>
                <div className="detail-row">
                  <strong>Autotask ID:</strong>{' '}
                  {selectedCompany.autotask_id ? (
                    <a
                      href={getAutotaskUrl(selectedCompany.autotask_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="autotask-link"
                    >
                      {selectedCompany.autotask_id} 🔗
                    </a>
                  ) : (
                    'Not synced'
                  )}
                </div>
                <div className="detail-row">
                  <strong>HubSpot ID:</strong>{' '}
                  {selectedCompany.hubspot_id ? (
                    <a
                      href={getHubSpotCompanyUrl(selectedCompany.hubspot_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="autotask-link"
                    >
                      {selectedCompany.hubspot_id} 🔗
                    </a>
                  ) : (
                    'Not synced'
                  )}
                </div>
              </div>

              {/* Sync Status Section */}
              <div className="detail-section">
                <h3>Sync Status</h3>
                <div className="detail-row">
                  <strong>Status:</strong>{' '}
                  <span className={`status-badge ${selectedCompany.sync_status}`}>
                    {getSyncStatusLabel(selectedCompany.sync_status)}
                  </span>
                </div>
                <div className="detail-row">
                  <strong>Last Synced:</strong> {formatDateTime(selectedCompany.last_sync_at)}
                </div>
                {selectedCompany.sync_message && (
                  <div className="detail-row">
                    <strong>Message:</strong>
                    <div className={
                      selectedCompany.sync_status === 'error'
                        ? 'error-message-box'
                        : 'success-message-box'
                    }>
                      {selectedCompany.sync_message}
                    </div>
                  </div>
                )}
              </div>

              {/* Sync History Section */}
              <div className="detail-section">
                <h3>Sync History</h3>
                {!syncHistory ? (
                  <button
                    className="btn-secondary"
                    onClick={() => loadSyncHistory(selectedCompany.hubspot_id || selectedCompany.autotask_id)}
                    disabled={loadingHistory}
                  >
                    {loadingHistory ? 'Loading...' : 'Load History'}
                  </button>
                ) : (
                  <div className="sync-history">
                    {syncHistory.length === 0 ? (
                      <div style={{padding: 12, textAlign: 'center', color: '#6B7280'}}>
                        No sync history available
                      </div>
                    ) : (
                      syncHistory.map((entry, idx) => (
                        <div key={idx} className="sync-log-entry">
                          <div>
                            <span className={`status-badge ${entry.sync_status}`}>
                              {entry.sync_status}
                            </span>
                            {entry.message && (
                              <div className="text-muted" style={{marginTop: 4, fontSize: 12}}>
                                {entry.message}
                              </div>
                            )}
                          </div>
                          <div className="sync-log-time">
                            <span className="text-muted" style={{fontSize: 12}}>
                              {formatDateTime(entry.created_at)}
                            </span>
                            {entry.execution_id && entry.workflow_id && (
                              <>
                                <span className="text-muted" style={{fontSize: 12}}> • </span>
                                <a
                                  href={`https://flow.ccassistant.app/workflow/${entry.workflow_id}/executions/${entry.execution_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="execution-link"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  View Execution
                                </a>
                              </>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Retry Sync Section */}
              <div className="detail-section">
                <h3>Actions</h3>
                <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 12 }}>
                  Manually trigger a sync for this company to HubSpot.
                </p>
                <button
                  className="btn btn-primary"
                  onClick={() => handleRetrySync(selectedCompany.hubspot_id || selectedCompany.autotask_id)}
                  disabled={loadingRetry}
                  style={{ width: '100%' }}
                >
                  <span>{loadingRetry ? '↻' : '↻'}</span>
                  {loadingRetry ? 'Syncing...' : 'Retry Sync'}
                </button>
                {retryResult && (
                  <div style={{
                    marginTop: 12,
                    padding: 12,
                    borderRadius: 6,
                    background: retryResult.success ? '#d1fae5' : '#fee2e2',
                    color: retryResult.success ? '#065f46' : '#991b1b',
                    fontSize: 13
                  }}>
                    {retryResult.success
                      ? 'Sync triggered successfully!'
                      : `Error: ${retryResult.error}`
                    }
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Companies;
